import React from 'react';
import {
  ClipboardDocumentListIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../UI/Card';
import { Alert, AlertDescription } from '../UI/Alert';
import LoadingSpinner from '../UI/LoadingSpinner';
import { useTicketStats } from '../../hooks/useTickets';
import { TicketStatus, TicketPriority, TicketCategory } from '../../types/ticket';
import { useTranslation } from 'react-i18next';
import { CHART_COLORS } from '../../utils/chartColors';

interface TicketDashboardProps {
  companyId?: number;
}

const StatCard = ({
  title,
  value,
  Icon,
  color,
}: {
  title: string;
  value: number | string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  color: string;
}) => (
  <Card>
    <CardContent className='flex items-center justify-between'>
      <div>
        <p className='text-sm text-gray-500 dark:text-gray-400'>{title}</p>
        <p className='text-3xl font-semibold text-gray-900 dark:text-white mt-1'>{value}</p>
      </div>
      <Icon className='h-10 w-10 shrink-0' style={{ color }} />
    </CardContent>
  </Card>
);

const Distribution = <K extends string>({
  title,
  entries,
  labelMap,
  colorMap,
}: {
  title: string;
  entries: [K, number][];
  labelMap: Record<K, string>;
  colorMap: Record<K, string>;
}) => (
  <div className='flex-1 min-w-[300px]'>
    <Card>
      <CardContent>
        <h3 className='text-base font-semibold text-gray-900 dark:text-white mb-3'>{title}</h3>
        <div className='space-y-2'>
          {entries.map(([key, count]) => (
            <div key={key} className='flex items-center justify-between'>
              <span
                className='inline-block min-w-[120px] rounded-full px-2.5 py-1 text-center text-xs font-medium text-white'
                style={{ backgroundColor: colorMap[key] }}
              >
                {labelMap[key]}
              </span>
              <span className='text-lg font-semibold text-gray-900 dark:text-white'>{count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

const statusColors = {
  [TicketStatus.OPEN]: CHART_COLORS.primary,
  [TicketStatus.IN_PROGRESS]: CHART_COLORS.warning,
  [TicketStatus.WAITING_CLIENT]: CHART_COLORS.warningLight,
  [TicketStatus.RESOLVED]: CHART_COLORS.success,
  [TicketStatus.CLOSED]: CHART_COLORS.gray,
};

const priorityColors = {
  [TicketPriority.LOW]: CHART_COLORS.success,
  [TicketPriority.MEDIUM]: CHART_COLORS.warning,
  [TicketPriority.HIGH]: CHART_COLORS.danger,
  [TicketPriority.CRITICAL]: CHART_COLORS.dangerDark,
};

const categoryColors = {
  [TicketCategory.SECURITY_INCIDENT]: CHART_COLORS.danger,
  [TicketCategory.FALSE_POSITIVE]: CHART_COLORS.warning,
  [TicketCategory.DATA_REQUEST]: CHART_COLORS.primary,
  [TicketCategory.TECHNICAL_SUPPORT]: CHART_COLORS.primaryLight,
  [TicketCategory.FEATURE_REQUEST]: CHART_COLORS.success,
  [TicketCategory.OTHER]: CHART_COLORS.gray,
};

export const TicketDashboard: React.FC<TicketDashboardProps> = ({ companyId }) => {
  const { stats, loading, error } = useTicketStats(companyId);
  const { t } = useTranslation();

  const statusLabels = {
    [TicketStatus.OPEN]: t('tickets.status.open'),
    [TicketStatus.IN_PROGRESS]: t('tickets.status.in_progress'),
    [TicketStatus.WAITING_CLIENT]: t('tickets.status.waiting_client'),
    [TicketStatus.RESOLVED]: t('tickets.status.resolved'),
    [TicketStatus.CLOSED]: t('tickets.status.closed')
  } as const;

  const priorityLabels = {
    [TicketPriority.LOW]: t('tickets.priority.low'),
    [TicketPriority.MEDIUM]: t('tickets.priority.medium'),
    [TicketPriority.HIGH]: t('tickets.priority.high'),
    [TicketPriority.CRITICAL]: t('tickets.priority.critical')
  } as const;

  const categoryLabels = {
    [TicketCategory.SECURITY_INCIDENT]: t('tickets.category.security_incident'),
    [TicketCategory.FALSE_POSITIVE]: t('tickets.category.false_positive'),
    [TicketCategory.DATA_REQUEST]: t('tickets.category.data_request'),
    [TicketCategory.TECHNICAL_SUPPORT]: t('tickets.category.technical_support'),
    [TicketCategory.FEATURE_REQUEST]: t('tickets.category.feature_request'),
    [TicketCategory.OTHER]: t('tickets.category.other')
  } as const;

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-[200px]'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant='destructive'>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert>
        <AlertDescription>{t('tickets.dashboard.noStats')}</AlertDescription>
      </Alert>
    );
  }

  // Fallbacks para evitar erros quando alguns campos não vêm do backend
  const byStatus = stats.by_status || {};
  const byPriority = stats.by_priority || {};
  const byCategory = stats.by_category || {};
  const totalTickets = stats.total_tickets ?? (stats as any).total ?? 0;

  return (
    <div>
      <h2 className='text-xl font-semibold text-gray-900 dark:text-white mb-4'>
        {t('tickets.dashboard.title')}
      </h2>

      <div className='grid gap-4 mb-6' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
        <StatCard title={t('tickets.dashboard.total')} value={totalTickets} Icon={ClipboardDocumentListIcon} color={CHART_COLORS.primary} />
        <StatCard
          title={t('tickets.dashboard.open')}
          value={byStatus[TicketStatus.OPEN] || 0}
          Icon={ExclamationCircleIcon}
          color={statusColors[TicketStatus.OPEN]}
        />
        <StatCard
          title={t('tickets.dashboard.in_progress')}
          value={byStatus[TicketStatus.IN_PROGRESS] || 0}
          Icon={ClockIcon}
          color={statusColors[TicketStatus.IN_PROGRESS]}
        />
        <StatCard
          title={t('tickets.dashboard.resolved')}
          value={byStatus[TicketStatus.RESOLVED] || 0}
          Icon={CheckCircleIcon}
          color={statusColors[TicketStatus.RESOLVED]}
        />
      </div>

      <div className='flex gap-4 mb-6 flex-wrap'>
        <Distribution
          title={t('tickets.dashboard.distributionByStatus')}
          entries={Object.entries(byStatus) as [TicketStatus, number][]}
          labelMap={statusLabels}
          colorMap={statusColors as Record<TicketStatus, string>}
        />
        <Distribution
          title={t('tickets.dashboard.distributionByPriority')}
          entries={Object.entries(byPriority) as [TicketPriority, number][]}
          labelMap={priorityLabels}
          colorMap={priorityColors as Record<TicketPriority, string>}
        />
      </div>

      {/* Distribuição por Categoria */}
      <div className='mb-6'>
        <Card>
          <CardContent>
            <h3 className='text-base font-semibold text-gray-900 dark:text-white mb-3'>
              {t('tickets.dashboard.distributionByCategory')}
            </h3>
            <div className='grid gap-3' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              {Object.entries(byCategory).map(([category, count]) => (
                <div key={category} className='text-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg'>
                  <p className='text-2xl font-semibold text-primary-600'>{count}</p>
                  <p className='text-sm text-gray-500 dark:text-gray-400'>
                    {categoryLabels[category as TicketCategory] || category}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className='flex gap-4 flex-wrap'>
        <div className='flex-1 min-w-[300px]'>
          <Card>
            <CardContent className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{t('tickets.dashboard.avgResolutionTime')}</p>
                <p className='text-xl font-semibold text-gray-900 dark:text-white mt-1'>
                  {stats.avg_resolution_time ? t('tickets.dashboard.hours', { hours: Math.round(stats.avg_resolution_time) }) : t('common.notAvailable')}
                </p>
              </div>
              <ArrowTrendingUpIcon className='h-10 w-10 shrink-0 text-primary-600' />
            </CardContent>
          </Card>
        </div>
        <div className='flex-1 min-w-[300px]'>
          <Card>
            <CardContent className='flex items-center justify-between'>
              <div>
                <p className='text-sm text-gray-500 dark:text-gray-400'>{t('tickets.dashboard.resolutionRate')}</p>
                <p className='text-xl font-semibold text-gray-900 dark:text-white mt-1'>
                  {totalTickets > 0 ? `${Math.round(((byStatus[TicketStatus.RESOLVED] || 0) / totalTickets) * 100)}%` : '0%'}
                </p>
              </div>
              <CheckCircleIcon className='h-10 w-10 shrink-0 text-success-600' />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
